import { FunctionComponent, PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { AnalyticsEvent, MatchEventDimensions, PostInteractionEventDimensions, WidgetEventDimensions, logAnalyticsEvent } from '@vestico/utils';

import { ClientReviewResponse, ProductBase, SizeRecommendationRequestResultProps, userDataCompleted } from '@vestico/model';

import { UserContext } from '@vestico/shared';

import { MatchingAPIContext, ProductContext, WidgetAuthContext } from '../../MainContext';
import { ConfigContext } from '../../WidgetEventsManager';
import { MatchButtonGlobalsContext } from '../../WidgetEventsManager';
import { useSizeRecommendations } from '../../hooks/useSizeRecommendations';
import { AvailableMatchingType, MatchingTypeProps } from '../../model/matchingType';
import { useMatchContextRegistry } from './MatchContextRegistry';

export interface MatchingResponsesProps {
  selectedResponseIndex: number | undefined;
  onSelectedReponseIndex: (responseIdx: number) => void;
}

export type MatchResponse = MatchingTypeProps & MatchingResponsesProps;

export const MatchResponsesContext = createContext<MatchResponse>({} as MatchResponse);

export interface MatchContextProps extends PropsWithChildren {
  availableMatchingTypes: AvailableMatchingType[];
  useInlineSizeRecommendation: boolean;
  product: ProductBase | null | undefined;
}

export const MatchContext: FunctionComponent<MatchContextProps> = ({ availableMatchingTypes, useInlineSizeRecommendation, product, children }) => {
  const { clientId } = useContext(ConfigContext);
  const api = useContext(MatchingAPIContext);

  const { context } = useMatchContextRegistry({ productUID: product?.uid });
  const { registerMatchContext, unregisterMatchContext } = context;

  const { selectedVariantUID } = useContext(ProductContext);
  const { user, uid: userUID } = useContext(UserContext);
  const { authInfo, resetAuth } = useContext(WidgetAuthContext);

  const [reviewResponses, setReviewResponses] = useState<ClientReviewResponse[] | undefined | null>(undefined);
  const [selectedResponseIndex, setSelectedResponseIndex] = useState<number | undefined>(undefined);
  const onSelectedReponseIndex = useCallback((idx: number) => setSelectedResponseIndex(idx), []);

  const [loading, setLoading] = useState(false);
  const { matchButtonOpen } = useContext(MatchButtonGlobalsContext);

  const hasContentMatchAvailable = useMemo(() => availableMatchingTypes.includes('content'), [availableMatchingTypes]);
  const hasSizeRecAvailable = useMemo(() => availableMatchingTypes.includes('size-rec'), [availableMatchingTypes]);

  const [activeMatchingType, setActiveMatchingType] = useState<AvailableMatchingType>(
    hasSizeRecAvailable && !hasContentMatchAvailable ? 'size-rec' : 'content'
  );

  const [defaultMatchingType, setDefaultMatchingType] = useState<AvailableMatchingType>(activeMatchingType);

  const [fitMatchSizeRecommendationResult, setFitMatchSizeRecommendationResult] = useState<SizeRecommendationRequestResultProps | undefined>(
    undefined
  );

  const { sizeRecommendationResult } = useSizeRecommendations({
    product,
    selectedVariantUID,
    enabled: hasSizeRecAvailable,
    force: ((activeMatchingType === 'size-rec' && matchButtonOpen) || useInlineSizeRecommendation) && !fitMatchSizeRecommendationResult,
  });

  const loadMatchesIfNeeded = useCallback(async () => {
    if (
      !matchButtonOpen ||
      !user ||
      !userUID ||
      !userDataCompleted(user, product?.matchingCategory, false, true) ||
      !product ||
      !hasContentMatchAvailable
    ) {
      setReviewResponses(undefined);
      setSelectedResponseIndex(undefined);
      return;
    }

    if (reviewResponses !== undefined) {
      setLoading(false);
      return;
    }

    if (loading || !api) {
      return;
    }

    console.debug('Start fetching body doubles.');

    setLoading(true);

    const fetchedReviewRequestResult = await api.fetchMatches({
      product,
      selectedVariantUID,
      user,
      authRequestInfo: { authInfo: authInfo, resetAuth },
    });
    const fetchedReviews = fetchedReviewRequestResult?.reviews;
    const sizeRecResult = fetchedReviewRequestResult?.sizeRecommendationResult;

    setReviewResponses(fetchedReviews ?? null);
    setSelectedResponseIndex(fetchedReviews && fetchedReviews.length > 0 ? 0 : undefined);
    setFitMatchSizeRecommendationResult(sizeRecResult);

    if (!!sizeRecResult?.recommendations && sizeRecResult.recommendations.length > 0) {
      setDefaultMatchingType('size-rec');
      setActiveMatchingType('size-rec');
    }

    if (fetchedReviews) {
      console.debug(`finished loading ${fetchedReviews.length} body doubles`);
    }

    setLoading(false);
  }, [matchButtonOpen, user, userUID, product, hasContentMatchAvailable, reviewResponses, loading, api, selectedVariantUID, authInfo, resetAuth]);

  useEffect(() => {
    loadMatchesIfNeeded();
  }, [reviewResponses, authInfo, loading, user, userUID, matchButtonOpen]);

  useEffect(() => {
    if (reviewResponses) {
      // reset body doubles
      setReviewResponses(undefined);
      return;
    }

    loadMatchesIfNeeded();
  }, [product]);

  const onActiveMatchingTypeChange = useCallback((newType: AvailableMatchingType) => setActiveMatchingType(newType), []);

  //
  //  ANALYTICS //
  //

  useEffect(() => {
    // not fetched yet
    if (reviewResponses === undefined || !product) {
      return;
    }

    const dimensions: MatchEventDimensions = {
      count: reviewResponses ? reviewResponses.length : 0,
      item_id: product.documentID,
      name: product.name,
      brand: product.brandName,
      provider: clientId,
    };

    logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.showMatches, dimensions });

    if (reviewResponses !== null && reviewResponses.length === 0) {
      const noDoublesParams: WidgetEventDimensions = {
        item_id: product.documentID,
        name: product.name,
        brand: product.brandName,
        provider: clientId,
      };

      logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.noMatches, dimensions: noDoublesParams });
    }
  }, [reviewResponses]);

  useEffect(() => {
    if (!sizeRecommendationResult || !product) {
      return;
    }

    const dimensions: WidgetEventDimensions = {
      item_id: product.documentID,
      name: product.name,
      brand: product.brandName,
      provider: clientId,
    };

    logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.sizeRecommendation, dimensions });
  }, [sizeRecommendationResult]);

  useEffect(() => {
    if (reviewResponses && reviewResponses.length > 0 && selectedResponseIndex !== undefined && product) {
      const response = reviewResponses[selectedResponseIndex];
      const dimensions: PostInteractionEventDimensions = {
        item_id: product.documentID,
        name: product.name,
        brand: product.brandName,
        provider: clientId,
        post_id: response?.postDocumentID,
        index: selectedResponseIndex,
        last_post_index: reviewResponses.length - 1,
        post_media_type: response?.media.mainMimeType,
      };
      logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.goToPost, dimensions });
    }
  }, [reviewResponses, selectedResponseIndex]);

  const matchResponsesContextValue = useMemo(() => {
    const matchResponse: MatchResponse = {
      activeMatchingType,
      defaultMatchingType,
      responses: reviewResponses,
      selectedResponseIndex,
      onSelectedReponseIndex,
      hasContentMatchAvailable,
      hasSizeRecAvailable,
      sizeRecommendation: fitMatchSizeRecommendationResult ?? sizeRecommendationResult,
      onActiveMatchingTypeChange,
    };
    return matchResponse;
  }, [
    activeMatchingType,
    defaultMatchingType,
    reviewResponses,
    selectedResponseIndex,
    onSelectedReponseIndex,
    hasContentMatchAvailable,
    hasSizeRecAvailable,
    fitMatchSizeRecommendationResult,
    sizeRecommendationResult,
    onActiveMatchingTypeChange,
  ]);

  useEffect(() => {
    if (product?.uid) {
      registerMatchContext(product.uid, matchResponsesContextValue);
      return () => {
        unregisterMatchContext(product.uid);
      };
    }
    return undefined;
  }, [product?.uid, matchResponsesContextValue, registerMatchContext, unregisterMatchContext]);

  return <MatchResponsesContext.Provider value={matchResponsesContextValue}>{children}</MatchResponsesContext.Provider>;
};
